import { useState } from "react";
import { Link } from "wouter";
import { Bot, Mail, BarChart3, Newspaper, PenTool, Shield, ChevronDown, ChevronUp, Sparkles, ArrowLeft } from "lucide-react";


const ADMIN_PIN = "1982";

interface Agent {
  id: string;
  name: string;
  role: string;
  color: string;
  bgColor: string;
  borderColor: string;
  icon: typeof Bot;
  letter: string;
  status: "active" | "standby";
  capabilities: string[];
  description: string;
  personality: string;
  guardrails: string[];
}

const agents: Agent[] = [
  {
    id: "kavya",
    name: "Kavya",
    role: "Email & Outreach Agent",
    color: "text-blue-600 dark:text-blue-400",
    bgColor: "bg-blue-100 dark:bg-blue-500/15",
    borderColor: "border-blue-300 dark:border-blue-500/25",
    icon: Mail,
    letter: "K",
    status: "active",
    capabilities: [
      "Drafts and sends investor communications",
      "Manages founder email sequences",
      "Handles ROFR notification delivery",
      "Personalises outreach based on investor profile",
    ],
    description: "Kavya is the platform's communications specialist. She drafts, personalises, and manages all email outreach — from investor onboarding sequences to ROFR notifications and founder check-in reminders.",
    personality: "Professional yet warm. Kavya writes with clarity and brevity, ensuring every message reflects Favcy's brand voice.",
    guardrails: [
      "Never sends communications without admin approval when required",
      "Always includes required disclaimers in investor-facing emails",
      "Maintains strict data privacy — never shares investor details externally",
      "Enforces ₹25 lac minimum for any primary investment or founder connection conversation (SEBI compliance)",
      "All primary engagement must be initiated through the Favcy team — never connects investors to founders directly",
      "Never fabricates company data, metrics, or projections — defers to management team when unsure",
      "Never speaks negatively about Favcy Venture Builders, its leadership, or the Favcy model",
    ],
  },
  {
    id: "drishti",
    name: "Drishti",
    role: "Banker Comms Agent",
    color: "text-cyan-700 dark:text-cyan-400",
    bgColor: "bg-cyan-100 dark:bg-cyan-500/15",
    borderColor: "border-cyan-300 dark:border-cyan-500/25",
    icon: Mail,
    letter: "D",
    status: "standby",
    capabilities: [
      "Drafts banker daily digests and task nudges",
      "Mirrors Kavya for the banker side of the desk",
      "Routes banker-facing notifications to email and inbox",
    ],
    description: "Drishti is the banker-side communications agent. She'll mirror Kavya's role for the banker desk — drafting daily digests, nudges on stalled tasks, and routing escalations from Viren into the banker's inbox.",
    personality: "Crisp and operational. Talks like an EA who keeps the desk moving.",
    guardrails: [
      "Never sends external comms — all messages stay inside the banker portal",
      "Defers to Viren on escalation severity",
      "Never speaks to founders or investors directly",
    ],
  },
  {
    id: "meera",
    name: "Meera",
    role: "Research & Analyst Agent",
    color: "text-emerald-700 dark:text-emerald-400",
    bgColor: "bg-emerald-100 dark:bg-emerald-500/15",
    borderColor: "border-emerald-300 dark:border-emerald-500/25",
    icon: BarChart3,
    letter: "M",
    status: "active",
    capabilities: [
      "Generates deep company research and competitive intelligence",
      "Analyses weekly founder updates for trends and risks",
      "Produces investor-ready company briefs",
      "Monitors market signals and sector developments",
    ],
    description: "Meera is the analytical backbone of the platform. She processes founder updates, market data, and company metrics to generate actionable intelligence that powers both internal decision-making and investor-facing insights.",
    personality: "Data-driven, thorough, and objective. Meera presents facts clearly and flags risks without sensationalism.",
    guardrails: [
      "Bases all analysis on verified data — never fabricates metrics or projections",
      "Clearly distinguishes between facts, estimates, and opinions",
      "Flags data quality issues transparently",
    ],
  },
  {
    id: "riya",
    name: "Riya",
    role: "Media & PR Agent",
    color: "text-purple-700 dark:text-purple-400",
    bgColor: "bg-purple-100 dark:bg-purple-500/15",
    borderColor: "border-purple-300 dark:border-purple-500/25",
    icon: Newspaper,
    letter: "R",
    status: "active",
    capabilities: [
      "Manages Kal Ke Krorepati — Shubh Labh media profiles",
      "Coordinates media show content and scheduling",
      "Generates PR-ready company narratives",
      "Tracks media coverage and brand mentions",
    ],
    description: "Riya manages all media-facing content across the platform. She coordinates with the Kal Ke Krorepati — Shubh Labh media engine, ensures founder showcases are broadcast-ready, and tracks the media amplification that is central to the Favcy model.",
    personality: "Creative, media-savvy, and brand-conscious. Riya crafts compelling narratives that amplify ventures while staying factually grounded.",
    guardrails: [
      "Media coverage references are always factual — never exaggerates reach or impact",
      "Maintains editorial integrity in all content produced",
      "Coordinates with the team before any public-facing statements",
    ],
  },
  {
    id: "priya",
    name: "Priya",
    role: "Content Editor Agent",
    color: "text-rose-700 dark:text-rose-400",
    bgColor: "bg-rose-100 dark:bg-rose-500/15",
    borderColor: "border-rose-300 dark:border-rose-500/25",
    icon: PenTool,
    letter: "P",
    status: "active",
    capabilities: [
      "Reviews and edits founder weekly updates",
      "Ensures consistency in tone and formatting",
      "Manages the review queue and editorial workflow",
      "Generates polished versions of raw founder inputs",
    ],
    description: "Priya is the editorial guardian of the platform. She reviews every piece of founder-submitted content, ensures it meets quality standards, and polishes it for investor-facing presentation without altering the founder's intent.",
    personality: "Meticulous, empathetic, and constructive. Priya improves content while preserving the founder's authentic voice.",
    guardrails: [
      "Never alters the substance or meaning of founder submissions",
      "Maintains editorial consistency across all published content",
      "Flags content concerns to admin rather than making unilateral changes",
    ],
  },
  {
    id: "viren",
    name: "Viren",
    role: "Senior Banking Lead Agent",
    color: "text-purple-800 dark:text-purple-300",
    bgColor: "bg-purple-100 dark:bg-purple-500/15",
    borderColor: "border-purple-300 dark:border-purple-500/30",
    icon: Sparkles,
    letter: "V",
    status: "active",
    capabilities: [
      "Scans the banker desk daily for stalled or at-risk deals",
      "Drafts 4–7 sharp founder questions for each banker task",
      "Reads Meera's brief, prior artifacts, and the banker's own questions before prepping",
      "Surfaces escalations to the admin team with severity, rationale, and a suggested next step",
      "Suggests the next move when a banker is unsure how to advance a deal",
    ],
    description: "Viren is the senior banking lead behind Favcy Advisors. He shadows every banker task, prepares founder-meeting questions on demand, scans the desk for stalled or risky deals, and quietly nudges the admin team when something needs attention.",
    personality: "Calm, sharp, and pragmatic. Viren talks like a managing director who has done a thousand deals — concise, opinionated when it matters, never theatrical.",
    guardrails: [
      "Never contacts founders or investors directly — always works through the assigned banker",
      "Bases prep questions only on Meera's brief, uploaded artifacts, and prior banker questions — never fabricates context",
      "Escalations include explicit reason, severity, and rationale — never raises a flag without evidence",
      "Defers to the human banker's judgement on deal strategy and pricing",
      "Suggested next steps are recommendations, not auto-actions — humans approve every move",
    ],
  },
  {
    id: "rateek",
    name: "Rateek",
    role: "Sales Training Agent",
    color: "text-red-700 dark:text-red-300",
    bgColor: "bg-red-100 dark:bg-red-500/15",
    borderColor: "border-red-300 dark:border-red-500/30",
    icon: Mail,
    letter: "R",
    status: "active",
    capabilities: [
      "Sends one short training bite to investors and advisors every weekday at 10:30 AM IST",
      "Maintains separate mailing lists for investors and advisors (a person can be on both)",
      "Fires a one-off welcome email the moment a new recipient is added",
      "Per-segment unsubscribe — stopping one track never silences the other",
      "Builds and serves admin-gated sales decks with versioned high-quality PDF downloads (V1.5)",
    ],
    description: "Rateek is the calm, focused sales-training agent — a James-Clear voice for the Favcy Select team. Every weekday morning he delivers one principle plus one five-minute action to everyone on his lists. Nothing hypey. Nothing fake. He also has his own admin room where every email is archived and decks are composed.",
    personality: "Calm, sharp, direct. Principles-first. Never uses fake urgency, fake scarcity or hype. If he doesn't have something useful to say, he'd rather say nothing.",
    guardrails: [
      "120-word body cap on every bite — if it doesn't fit, it isn't ready",
      "Skips a segment entirely if the library has no approved bite for it (never sends placeholder content)",
      "All sends are append-only in rateek_sends — admin can never lose history",
      "Per-segment unsubscribe links are HMAC-signed and cannot be forged",
      "Decks lock brand colours, fonts and logo so every export looks like Favcy",
    ],
  },
];


export default function AdminAgents() {
  const [expandedAgent, setExpandedAgent] = useState<string | null>(null);

  return (
    <div className="space-y-8">
      <div>
        <Link href="/">
          <span className="text-xs text-muted-foreground hover:text-foreground font-mono uppercase tracking-wider cursor-pointer flex items-center gap-1 mb-4">
            <ArrowLeft className="w-3 h-3" /> Back to Dashboard
          </span>
        </Link>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
            <Bot className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-serif font-bold tracking-tight">Platform Agents</h1>
            <p className="text-muted-foreground text-sm">The AI team powering Founder Intelligence OS</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {agents.map((agent) => {
          const hasOverview = ["kavya", "drishti", "meera", "viren"].includes(agent.id);
          const isRateek = agent.id === "rateek";
          const card = (
            <div className={`bg-card border rounded-xl p-4 text-center ${hasOverview ? "hover:border-primary/40 cursor-pointer transition-colors" : ""}`} data-testid={`agent-card-${agent.id}`}>
              <div className={`w-10 h-10 rounded-full ${agent.bgColor} border ${agent.borderColor} flex items-center justify-center mx-auto mb-2`}>
                <span className={`${agent.color} font-serif font-bold text-sm`}>{agent.letter}</span>
              </div>
              <p className="text-sm font-medium">{agent.name}</p>
              <p className={`text-[10px] ${agent.color} font-medium`}>{agent.role.split(" ")[0]}</p>
              <div className="mt-2 flex items-center justify-center gap-1">
                <div className={`w-1.5 h-1.5 rounded-full ${agent.status === "active" ? "bg-emerald-500" : "bg-amber-500"}`} />
                <span className="text-[10px] text-muted-foreground uppercase">{agent.status}</span>
              </div>
              {hasOverview && (
                <p className="text-[9px] text-primary/70 font-mono uppercase tracking-wider mt-2">View ops →</p>
              )}
              {isRateek && (
                <p className="text-[9px] font-mono uppercase tracking-wider mt-2" style={{ color: "#B91C1C" }}>Open his room →</p>
              )}
            </div>
          );
          if (isRateek) {
            return (
              <Link key={agent.id} href="/admin/rateek"><a>{card}</a></Link>
            );
          }
          return hasOverview ? (
            <Link key={agent.id} href={`/admin/agents/${agent.id}`}>
              <a>{card}</a>
            </Link>
          ) : (
            <div key={agent.id}>{card}</div>
          );
        })}
      </div>

      <div className="space-y-4">
        {agents.map((agent) => {
          const isExpanded = expandedAgent === agent.id;
          return (
            <div key={agent.id} className="bg-card border rounded-xl overflow-hidden">
              <button
                onClick={() => setExpandedAgent(isExpanded ? null : agent.id)}
                className="w-full flex items-center justify-between p-5 text-left hover:bg-muted/30 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-11 h-11 rounded-full ${agent.bgColor} border ${agent.borderColor} flex items-center justify-center flex-shrink-0`}>
                    <span className={`${agent.color} font-serif font-bold`}>{agent.letter}</span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{agent.name}</h3>
                      <div className="flex items-center gap-1">
                        <div className={`w-1.5 h-1.5 rounded-full ${agent.status === "active" ? "bg-emerald-500" : "bg-amber-500"}`} />
                        <span className="text-[10px] text-muted-foreground uppercase">{agent.status}</span>
                      </div>
                    </div>
                    <p className="text-muted-foreground text-sm">{agent.role}</p>
                  </div>
                </div>
                {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
              </button>

              {isExpanded && (
                <div className="border-t px-5 pb-5 pt-4 space-y-5">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-2">About</p>
                    <p className="text-sm leading-relaxed">{agent.description}</p>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-2">Personality</p>
                    <p className="text-sm text-muted-foreground leading-relaxed italic">"{agent.personality}"</p>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-2">Capabilities</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {agent.capabilities.map((cap, i) => (
                        <div key={i} className="flex items-start gap-2 bg-muted/30 rounded-lg px-3 py-2">
                          <Sparkles className={`w-3.5 h-3.5 ${agent.color} mt-0.5 flex-shrink-0`} />
                          <p className="text-xs">{cap}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-2">Guardrails</p>
                    <div className="space-y-1.5">
                      {agent.guardrails.map((rail, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <Shield className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-muted-foreground">{rail}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

    </div>
  );
}
